import { refreshClaims, uiNamespace } from '@backoffice-frontend/common'
import { useErrorHandlingMutation } from '@backoffice-frontend/data-fetching'
import {
  MoiaGrid,
  useMoiaErrorNotification,
  useMoiaSuccessNotification,
} from '@backoffice-frontend/patterns'
import { useClaims } from '@backoffice-frontend/restricted'
import {
  type BackofficeGroups,
  useBackofficeGroups,
  useBackofficeLoggedInUser,
} from '@backoffice-frontend/user-management-ui'
import { BoumRight, BoumRightEnDeCoder } from '@moia-dev/moia-token-claims'
import {
  ActionItemMultiSelect,
  type ActionMenuProps,
  Button,
  LoadingIndicator,
  SelectionMenu,
} from '@moia-dev/pace-web'
import { t } from 'i18next'
import { useEffect, useState } from 'react'

import store from 'store'
import { MenuState, type MenuStateType } from '../MenuState'
import { ResetUserButton, persistUser } from './ResetUser'
import { useBackofficeUserManagementSwapGroupsMutation } from './swapUserWithGroups.hook'

const getGroupsByRight = (groups: BackofficeGroups, rightName: string) =>
  groups.filter(group => group.rights?.some(right => right === rightName))

type RightNamesByAreas =
  | 'AREA_EMPLOYEES'
  | 'AREA_VEHICLES'
  | 'AREA_VEHICLE_GROUPS'
  | 'AREA_CUSTOM_ROAD_BLOCKAGES'
  | 'AREA_CUSTOMERS'
  | 'AREA_SERVICE_AREA'
  | 'AREA_ALERTS_TESTING'

const getPathName = (): RightNamesByAreas | string => {
  const href = window.location.pathname.split('/')
  if (href[1] === 'sam') {
    return href[2]
  }
  return href[1]
}

const getRightName = () => {
  const rightName = `AREA_${getPathName()
    .split('-')
    .map(e => e.toUpperCase())
    .join('_')}`
  const d = (f: BoumRight) => BoumRightEnDeCoder.fromEnum(f)!
  switch (rightName) {
    case 'AREA_EMPLOYEES':
      return d(BoumRight.AREA_EMPLOYEE)
    case 'AREA_VEHICLES':
      return d(BoumRight.AREA_VEHICLE)
    case 'AREA_VEHICLE_GROUPS':
      return d(BoumRight.AREA_VEHICLE_GROUP)
    case 'AREA_CUSTOM_ROAD_BLOCKAGES':
      return d(BoumRight.AREA_CUSTOM_STREET_ROUTING)
    case 'AREA_CUSTOMERS':
      return d(BoumRight.AREA_CUSTOMER)
    case 'AREA_SERVICE_AREA':
      return d(BoumRight.AREA_SERVICE_AREAS)
    case 'AREA_ALERTS_TESTING':
      return d(BoumRight.AREA_TEST)
    default:
      return rightName
  }
}

export const AccountBadgeAvailableGroupsMenu = ({
  setCurrentMenu,
  props,
}: {
  setCurrentMenu: (menu: MenuStateType) => void
  props: unknown
}) => {
  const [groups, setGroups] = useState<BackofficeGroups>([])
  const [isOpen] = useState(true)
  const [swapGroups] = useErrorHandlingMutation(
    useBackofficeUserManagementSwapGroupsMutation,
  )
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const { data, loading } = useBackofficeGroups()
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()
  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification()
  const { setClaims } = useClaims()
  const { user } = useBackofficeLoggedInUser()

  const GROUP_SWITCHER_FALLBACK = `${uiNamespace}.groupSwitcherReset`
  const initialGroups: string[] = store.get(GROUP_SWITCHER_FALLBACK)

  const userGroups = user?.timedGroups

  useEffect(() => {
    if (userGroups) {
      setSelected(userGroups.map(group => group.groupName))
    }
  }, [userGroups])

  persistUser(user, initialGroups)

  const rightName = getRightName()

  useEffect(() => {
    if (data) {
      const groups = getGroupsByRight(data, rightName)
      setGroups(groups)
    }
  }, [data, rightName])

  const visibleGroups = groups.length > 0 ? groups : data

  const handleRefreshToken = async () => {
    try {
      const claims = await refreshClaims()
      if (claims) {
        setClaims(claims)
        // reload page to update JWT token
        window.location.reload()
      } else {
        enqueueMoiaErrorNotification(
          t('authenticatedAppView_rightsAndPermissionRefreshFailed'),
        )
      }
    } catch {
      enqueueMoiaErrorNotification(
        t('authenticatedAppView_rightsAndPermissionRefreshFailed'),
      )
    }
  }

  const handleAssignGroup = async (groupName: string[]) => {
    try {
      await swapGroups({
        variables: {
          input: {
            groupName: null,
            groupsList: groupName,
          },
        },
      })
      enqueueMoiaSuccessNotification(
        t('authenticatedAppView_groupsSwappedSuccess'),
      )
      await handleRefreshToken()
    } catch (e) {
      enqueueMoiaErrorNotification(e)
    }
  }

  useEffect(() => {
    if (!isOpen) {
      setCurrentMenu(MenuState.main)
    }
  }, [isOpen, setCurrentMenu])

  const options =
    visibleGroups?.map((group, index) => ({
      index: index,
      label: group.displayName,
      value: group.name,
    })) ?? []

  const filteredOptions = options.filter(option =>
    query.length > 0
      ? option.label.toLowerCase().includes(query.toLowerCase())
      : true,
  )

  const selectedIsInitial = initialGroups
    ? selected.every(item => initialGroups.includes(item))
    : false

  return (
    <div
      onClick={
        // Since this is a submenu, a click on anything other than an ActionItem of the main menu would close everything. This prevents that.
        e => e.stopPropagation()
      }
    >
      <SelectionMenu
        {...(props as ActionMenuProps)}
        title={t('authenticatedAppView_applyGroup_header')}
        caption={t('authenticatedAppView_applyGroup_caption')}
        footer={
          <>
            <MoiaGrid column gridGap={1} css={{ gridAutoFlow: 'row' }}>
              <MoiaGrid>
                <Button
                  disabled={selected.length === 0}
                  label={t(
                    'authenticatedAppView_applyGroup_clearButtonLabel ({{currentSelected}})',
                    {
                      currentSelected: selected.length,
                    },
                  )}
                  onClick={() => {
                    setSelected([])
                  }}
                  variant="secondary"
                  size="compact"
                />
              </MoiaGrid>
              <MoiaGrid gridTemplateColumns="1fr 1fr" gridGap={1}>
                <ResetUserButton
                  handleRefreshToken={handleRefreshToken}
                  initialGroups={initialGroups}
                />
                <Button
                  disabled={selected.length === 0 || selectedIsInitial}
                  label={t('common_apply_label')}
                  onClick={e => {
                    e.stopPropagation()
                    handleAssignGroup(selected)
                  }}
                  size="compact"
                />
              </MoiaGrid>
            </MoiaGrid>
          </>
        }
        query={query}
        setQuery={value => {
          setQuery(value)
        }}
      >
        {loading ? (
          <LoadingIndicator />
        ) : (
          filteredOptions.map(option => {
            return (
              <ActionItemMultiSelect
                key={option.value}
                selected={selected.includes(option.value)}
                onClick={e => {
                  e.stopPropagation()
                  if (selected.includes(option.value)) {
                    setSelected(selected.filter(item => item !== option.value))
                  } else {
                    setSelected([...selected, option.value])
                  }
                }}
                label={option.label}
              />
            )
          })
        )}
      </SelectionMenu>
    </div>
  )
}
